// Verdict of the link builder: will the traffic of a link reach a landing? Works on the data of
// GET /satellite-domains/{id}/traffic and the picked route.

export const LEVELS = { ok: 0, warn: 1, danger: 2 };

// Returns { level: "ok" | "warn" | "danger", issues: [{ level, key, params }], receiving: [satellite ids] }.
// receiving: the satellites that get the traffic: the serving ones DNS points to, or all serving ones when DNS can't
// tell (no answer, or a proxy like Cloudflare in front).
export function checkTraffic(traffic, route) {
  const issues = [];
  const add = (level, key, params = {}) => issues.push({ level, key, params });

  const satellites = traffic.satellites || [];
  const dns = traffic.dns || { resolved: false, records: [] };
  const records = dns.records || [];

  if (!traffic.router) add("danger", "no_router");
  else if (route && !route.landings.length) add("danger", "no_landings", { path: route.path });

  if (!satellites.length) add("danger", "no_satellites");

  // A satellite that is still being installed has no address and gets no traffic
  const installed = satellites.filter((s) => s.ip);
  let receiving = installed.map((s) => s.id);
  if (!dns.resolved) {
    add("warn", "dns_failed");
  } else if (!records.length) {
    add("danger", "dns_empty");
    receiving = [];
  } else {
    const serving = records.filter((r) => r.serves);
    const foreignSatellites = records.filter((r) => r.satellite && !r.serves);
    const unknown = records.filter((r) => !r.satellite);
    if (serving.length) {
      receiving = installed.filter((s) => s.in_dns).map((s) => s.id);
      if (foreignSatellites.length || unknown.length) add("warn", "dns_partial", { count: foreignSatellites.length + unknown.length });
    } else if (foreignSatellites.length) {
      add("danger", "dns_wrong_satellite", { names: foreignSatellites.map((r) => r.satellite.name || r.satellite.hostname || r.ip).join(", ") });
      receiving = [];
    } else if (installed.length) {
      // Only addresses that are not satellites: a proxy in front, or DNS still points to the old server.
      add("warn", "dns_proxy");
    }
  }

  const offline = satellites.filter((s) => receiving.includes(s.id) && s.status === "offline");
  if (offline.length && offline.length === receiving.length) add("danger", "all_offline");
  else if (offline.length) add("warn", "some_offline", { names: offline.map((s) => s.name || s.hostname || s.ip).join(", ") });
  if (satellites.length && !installed.length) add("danger", "not_installed");

  if (!traffic.domain.certificate && !issues.some((i) => i.level === "danger")) add("info", "ssl_pending");

  const level = issues.reduce((max, issue) => (LEVELS[issue.level] > LEVELS[max] ? issue.level : max), "ok");
  return { level, issues, receiving };
}

// Query string value: encoded, but source macros like {clickid} stay as they are.
export function encodeValue(value) {
  return encodeURIComponent(value).replace(/%7B/gi, "{").replace(/%7D/gi, "}");
}

// Link parts for highlighting: [{ kind: "base" | "path" | "key" | "value" | "sep" | "placeholder", text }].
export function linkParts(host, route, platformValue, subValues) {
  const parts = [
    { kind: "base", text: `https://${host}` },
    { kind: "path", text: route.path === "/" ? "/" : route.path },
  ];
  const pairs = [];
  if (route.platform.mode === "param") pairs.push({ key: route.platform.param, value: platformValue, required: true });
  route.sub_params.forEach((name, index) => {
    if (name && subValues[index]) pairs.push({ key: name, value: subValues[index] });
  });
  pairs.forEach((pair, index) => {
    parts.push({ kind: "sep", text: index ? "&" : "?" });
    parts.push({ kind: "key", text: `${pair.key}=` });
    parts.push(pair.value === "" || pair.value === null || pair.value === undefined ? { kind: "placeholder", text: "…" } : { kind: "value", text: encodeValue(String(pair.value)) });
  });
  return parts;
}
